import useAuthenticatedFetch from "@/hooks/useAuthenticatedFetch.ts";
import type { CaseFilter } from "@/queries/useFetchCaseFiltersets.ts";
import {
    type DashboardCasesDocument,
    caseResourceSelector,
} from "@/queries/useFetchMrtDashboardCases.ts";
import { apiUrl } from "@/utils/api.ts";
import { type UseQueryResult, useQuery } from "@tanstack/react-query";
import type {
    MRT_ColumnFiltersState,
    MRT_PaginationState,
    MRT_SortingState,
} from "material-react-table";

type Props = {
    isQueryEnabled?: boolean;
    selectedTab?: string;
    columnFilters: MRT_ColumnFiltersState;
    globalFilter: string;
    activeFilter: CaseFilter | null;
    pagination: MRT_PaginationState;
    sorting: MRT_SortingState;
    searchFields: string[];
};

export const useFetchMrtCases = ({
    isQueryEnabled = true,
    selectedTab = "",
    columnFilters,
    globalFilter,
    activeFilter,
    pagination,
    sorting,
    searchFields,
}: Props): UseQueryResult<DashboardCasesDocument> => {
    const fetch = useAuthenticatedFetch();

    return useQuery({
        queryKey: [
            "case-table-data",
            columnFilters,
            globalFilter,
            pagination.pageIndex,
            pagination.pageSize,
            sorting,
            searchFields,
        ],
        enabled: isQueryEnabled,
        queryFn: async () => {
            const requestBody = {
                start: pagination.pageIndex * pagination.pageSize,
                size: pagination.pageSize,
                filters: columnFilters ?? [],
                globalFilter: globalFilter ?? "",
                activeFilter: activeFilter,
                sorting: sorting ?? [],
                selectedTab: selectedTab ?? "",
                searchFields: searchFields,
            };

            const response = await fetch(apiUrl("/case"), {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                },
                body: JSON.stringify(requestBody),
            });

            return await response.json();
        },
        select: caseResourceSelector,
        staleTime: 10_000,
    });
};
